import { EyeOff } from "lucide-react"
import { useState } from "react"
import { useTranslation } from "react-i18next"
import { useNavigate } from "react-router-dom"
import { CustomInput } from "@/components/forms/customInput"
import { EyeIcon } from "@/components/icons"
import { Button } from "@/components/ui/button"
import { type LoginFormData, loginSchema } from "@/features/auth/schemas/auth.schema"
import { useAuthStore } from "@/features/auth/store/authStore"
import { SocialLoginButtons } from "../../components/SocialLoginButtons"
import { useAuthForm } from "../../hooks/useAuthForm"

export function MobileLoginScreen() {
  const { t } = useTranslation("auth")
  const navigate = useNavigate()
  const login = useAuthStore(state => state.login)
  const [showPassword, setShowPassword] = useState(false)

  const { form, handleSubmit, isSubmitting, serverError, clearServerError } = useAuthForm(
    loginSchema,
    async (data: LoginFormData) => {
      const response = await login(data.email, data.password)

      if (!response.user.emailVerified) {
        navigate("/verify-email", { state: { email: data.email } })
        return
      }

      if (!response.user.onboardingCompleted) {
        navigate("/onboarding/permissions")
      } else {
        navigate("/")
      }
    },
  )

  const emailRegister = form.register("email")
  const passwordRegister = form.register("password")

  return (
    <form onSubmit={handleSubmit}>
      <div className="space-y-6">
        <div className="text-center mb-10">
          <h2 className="text-xl leading-normal font-semibold text-[#F2F0EBE6]">
            Login to Rituality
          </h2>
        </div>

        <div className="space-y-2.5">
          <div className="space-y-3.75">
            <CustomInput
              label={t("login.emailLabel")}
              type="email"
              placeholder={t("login.emailPlaceholder")}
              required={false}
              {...emailRegister}
              error={form.formState.errors.email?.message as string | undefined}
              disabled={isSubmitting}
              onChange={e => {
                emailRegister.onChange?.(e)
                clearServerError()
              }}
              className="pb-2.5"
            />

            <div className="relative">
              <CustomInput
                label={t("login.passwordLabel")}
                type={showPassword ? "text" : "password"}
                required={false}
                placeholder={t("login.passwordPlaceholder")}
                {...passwordRegister}
                error={
                  (form.formState.errors.password?.message || serverError) as string | undefined
                }
                disabled={isSubmitting}
                onChange={e => {
                  passwordRegister.onChange?.(e)
                  clearServerError()
                }}
                className="pb-1 pr-8"
              />
              <button
                type="button"
                onClick={() => setShowPassword(prev => !prev)}
                className="absolute right-1 top-2/3 -translate-y-1/2 flex items-center text-body cursor-pointer"
                aria-label={showPassword ? "Hide password" : "Show password"}
              >
                {showPassword ? (
                  <EyeOff size={17} className="text-primary" />
                ) : (
                  <EyeIcon size={17} />
                )}
              </button>
            </div>
          </div>

          <div className="text-right">
            <button
              type="button"
              className="text-[13px] text-[#F2F0EB80] bg-transparent border-none p-0 leading-[25px]"
              onClick={() => navigate("/forgot-password")}
            >
              Trouble signing in? <span className="font-semibold">Reset password</span>
            </button>
          </div>

          <div className="mt-2.5 mb-5">
            <Button
              type="submit"
              variant="primary"
              size="md"
              className="rounded-full uppercase text-sm font-semibold py-3.75 h-12.5"
              isLoading={isSubmitting}
              fullWidth
            >
              Login
            </Button>
          </div>

          <SocialLoginButtons isLoading={isSubmitting} />
        </div>
      </div>

      <div className="text-center pt-12.5 w-full space-y-6.5">
        <div className="text-center space-y-5">
          <p className="font-semibold text-sm leading-normal text-[#F2F0EBE6]">
            Don't have an account?
          </p>
          <Button
            type="button"
            variant="outline"
            size="md"
            className="rounded-full uppercase border-[#F2F0EB] text-[#F2F0EB] py-3.75 h-12.5"
            fullWidth
            onClick={() => navigate("/signup")}
          >
            Sign Up
          </Button>
        </div>
        <p className="text-sm text-[#F2F0EB66] leading-6.25">
          By continuing, you agree to our{" "}
          <a
            href="https://myrituality.com/terms/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold"
          >
            Terms & Conditions
          </a>{" "}
          and{" "}
          <a
            href="https://myrituality.com/privacy/"
            target="_blank"
            rel="noopener noreferrer"
            className="font-semibold"
          >
            Privacy Policy
          </a>
          .
        </p>
      </div>
    </form>
  )
}
